import { useState, useRef, useEffect, useCallback } from 'react'
import { CornerDownLeft, Paperclip, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { Message } from '../../types'

const THINKING_VERBS = [
  'Calculating toolpath',
  'Checking clearances',
  'Optimizing feeds',
  'Analyzing geometry',
  'Computing speeds',
  'Verifying depths',
  'Planning operations',
  'Evaluating strategy',
  'Mapping features',
  'Simulating passes',
  'Validating program',
  'Resolving coordinates',
]

function ThinkingPulse() {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex gap-1">
        {[0, 1, 2, 3, 4].map(i => (
          <motion.div
            key={i}
            className="w-1 rounded-full bg-cyan-400"
            animate={{
              height: [4, 16, 4],
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 0.8,
              repeat: Infinity,
              delay: i * 0.12,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.span
          key={useThinkingVerb(true)}
          initial={{ opacity: 0, x: 8 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -8 }}
          transition={{ duration: 0.25 }}
          className="font-mono text-xs text-cyan-400/80 tracking-wide"
        >
          {useThinkingVerb(true)}
        </motion.span>
      </AnimatePresence>
    </div>
  )
}

function useThinkingVerb(active: boolean) {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    if (!active) return
    setIndex(Math.floor(Math.random() * THINKING_VERBS.length))
    const id = setInterval(() => {
      setIndex(prev => {
        let next = prev
        while (next === prev) next = Math.floor(Math.random() * THINKING_VERBS.length)
        return next
      })
    }, 2500)
    return () => clearInterval(id)
  }, [active])

  return THINKING_VERBS[index]
}

interface ChatPanelProps {
  messages: Message[]
  isTyping: boolean
  sendMessage: (text: string, images?: File[]) => void | Promise<void>
  readOnly?: boolean
}

export default function ChatPanel({ messages, isTyping, sendMessage, readOnly }: ChatPanelProps) {
  const [input, setInput] = useState('')
  const [stagedImages, setStagedImages] = useState<File[]>([])
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const thinkingVerb = useThinkingVerb(isTyping)

  // Track whether the user has scrolled away from the bottom
  const isNearBottomRef = useRef(true)

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return
    // "Near bottom" = within 80px of the bottom edge
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }, [])

  // Only auto-scroll if the user hasn't scrolled up
  useEffect(() => {
    if (isNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  // Auto-resize textarea — grows up to 200px
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 200) + 'px'
    }
  }, [input])

  const handleSend = () => {
    if (!input.trim() && stagedImages.length === 0) return
    isNearBottomRef.current = true
    sendMessage(input, stagedImages.length > 0 ? stagedImages : undefined)
    setInput('')
    setStagedImages([])
  }

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length > 0) setStagedImages(prev => [...prev, ...files])
    e.target.value = ''
  }, [])

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items)
    const imageFiles = items
      .filter(item => item.type.startsWith('image/'))
      .map(item => item.getAsFile())
      .filter((f): f is File => f !== null)
    if (imageFiles.length > 0) {
      setStagedImages(prev => [...prev, ...imageFiles])
    }
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Check if the last message is a streaming AI message
  const lastMsg = messages[messages.length - 1]
  const isStreaming = isTyping && lastMsg?.role === 'assistant'

  return (
    <div className="flex flex-col h-full rounded-t-xl overflow-hidden glass">
      {/* Terminal header bar — matches CodeEditor tab bar height */}
      <div className="flex items-center gap-2 px-4 border-b border-cyan-500/10 flex-shrink-0" style={{ height: '2.25rem' }}>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-red-500/50" />
          <div className="w-2 h-2 rounded-full bg-yellow-500/50" />
          <div className="w-2 h-2 rounded-full bg-green-500/50" />
        </div>
        <span className="font-mono text-xs text-slate-500 tracking-wider ml-1">
          TRINITY
        </span>
        {/* Thinking indicator in header */}
        <AnimatePresence>
          {isTyping && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="ml-auto flex items-center gap-1.5 font-mono text-[10px] text-cyan-400/70"
            >
              <div className="flex gap-[2px]">
                {[0, 1, 2].map(i => (
                  <motion.div
                    key={i}
                    className="w-[3px] rounded-full bg-cyan-400"
                    animate={{ height: [3, 10, 3], opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 0.7, repeat: Infinity, delay: i * 0.1, ease: 'easeInOut' }}
                  />
                ))}
              </div>
              <AnimatePresence mode="wait">
                <motion.span
                  key={thinkingVerb}
                  initial={{ opacity: 0, x: 4 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -4 }}
                  transition={{ duration: 0.2 }}
                >
                  {thinkingVerb.toLowerCase()}
                </motion.span>
              </AnimatePresence>
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      {/* Terminal output */}
      <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center px-4 opacity-40">
            <p className="text-sm text-slate-500 font-mono leading-relaxed">
              Ask about your G-code or<br />request changes to the toolpath
            </p>
          </div>
        )}
        <AnimatePresence initial={false}>
          {messages.map(msg => {
            const isMsgThinking = msg.role === 'assistant' && msg.content === '' && isStreaming && msg.id === lastMsg?.id
            const isMsgStreaming = msg.role === 'assistant' && msg.content !== '' && isStreaming && msg.id === lastMsg?.id

            return (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15 }}
              >
                {msg.role === 'user' ? (
                  /* User input — highlighted prompt block */
                  <div className="flex items-start gap-2.5 rounded-lg bg-amber-400/[0.06] border border-amber-400/15 px-3 py-2">
                    <span className="font-mono text-sm font-bold text-amber-400 shrink-0 select-none leading-relaxed">&gt;</span>
                    <div className="min-w-0">
                      {msg.images && msg.images.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-1.5">
                          {msg.images.map((img, i) => (
                            <img
                              key={i}
                              src={`data:${img.mimeType};base64,${img.base64}`}
                              alt={img.name}
                              className="h-16 rounded border border-amber-400/20 object-cover"
                            />
                          ))}
                        </div>
                      )}
                      <p className="font-mono text-sm text-slate-100 leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ) : (
                  /* Assistant output — plain terminal text */
                  <div className="font-mono text-sm text-slate-400 leading-relaxed">
                    {isMsgThinking ? (
                      <ThinkingPulse />
                    ) : (
                      <div>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                            strong: ({ children }) => <strong className="font-semibold text-slate-200">{children}</strong>,
                            em: ({ children }) => <em className="italic text-slate-500">{children}</em>,
                            code: ({ children, className }) => {
                              const isBlock = className?.includes('language-')
                              if (isBlock) {
                                return (
                                  <code className="block my-2 p-2.5 rounded bg-black/40 border border-slate-700/50 font-mono text-[13px] text-cyan-300 overflow-x-auto whitespace-pre">
                                    {children}
                                  </code>
                                )
                              }
                              return (
                                <code className="px-1 py-0.5 rounded bg-black/30 font-mono text-[13px] text-cyan-300">
                                  {children}
                                </code>
                              )
                            },
                            pre: ({ children }) => <>{children}</>,
                            ul: ({ children }) => <ul className="list-disc pl-4 mb-2 last:mb-0 space-y-1 text-slate-400">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 last:mb-0 space-y-1 text-slate-400">{children}</ol>,
                            li: ({ children }) => <li className="pl-1">{children}</li>,
                            h1: ({ children }) => <h1 className="font-semibold text-base text-cyan-300 mt-3 mb-1.5 border-b border-slate-700/50 pb-1">{children}</h1>,
                            h2: ({ children }) => <h2 className="font-semibold text-[15px] text-cyan-300 mt-3 mb-1.5">{children}</h2>,
                            h3: ({ children }) => <h3 className="font-semibold text-sm text-cyan-400 mt-2 mb-1">{children}</h3>,
                            hr: () => <hr className="border-slate-700/50 my-3" />,
                            table: ({ children }) => (
                              <div className="my-2 overflow-x-auto rounded border border-slate-700/50">
                                <table className="w-full text-sm">{children}</table>
                              </div>
                            ),
                            thead: ({ children }) => <thead className="bg-slate-800/50 text-slate-300">{children}</thead>,
                            tbody: ({ children }) => <tbody className="text-slate-400">{children}</tbody>,
                            tr: ({ children }) => <tr className="border-b border-slate-700/30 last:border-0">{children}</tr>,
                            th: ({ children }) => <th className="px-2.5 py-1.5 text-left font-semibold whitespace-nowrap">{children}</th>,
                            td: ({ children }) => <td className="px-2.5 py-1.5 whitespace-nowrap">{children}</td>,
                            a: ({ href, children }) => (
                              <a href={href} target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline underline-offset-2 hover:text-cyan-300">
                                {children}
                              </a>
                            ),
                            blockquote: ({ children }) => (
                              <blockquote className="border-l-2 border-slate-600 pl-2.5 my-2 text-slate-500 italic">{children}</blockquote>
                            ),
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                        {isMsgStreaming && (
                          <span className="inline-block w-1.5 h-3.5 bg-cyan-400/60 animate-pulse rounded-sm ml-0.5 align-middle" />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            )
          })}
        </AnimatePresence>
        <div ref={messagesEndRef} />
      </div>

      {/* Chat input */}
      {!readOnly && (
        <div className="px-3 py-3">
          {/* Staged image thumbnails */}
          {stagedImages.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2 px-1">
              {stagedImages.map((file, i) => (
                <div key={i} className="relative group">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="h-14 rounded-lg border border-cyan-500/20 object-cover"
                  />
                  <button
                    onClick={() => setStagedImages(prev => prev.filter((_, j) => j !== i))}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-800 border border-slate-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3 text-slate-400" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2 rounded-xl border border-cyan-500/20 bg-white/[0.03] px-3 py-2 focus-within:border-cyan-500/40 transition-colors">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder="Ask about your toolpath..."
              rows={1}
              className="flex-1 bg-transparent font-mono text-sm text-slate-200 placeholder-slate-600 focus:outline-none resize-none leading-relaxed overflow-hidden"
              style={{ minHeight: '1.5rem', maxHeight: '200px' }}
            />
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="sr-only" onChange={handleFileSelect} />
            <div className="flex items-center gap-1 shrink-0 pb-0.5">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-1.5 rounded-lg text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all"
                title="Attach image"
              >
                <Paperclip className="w-4 h-4" />
              </button>
              <button
                onClick={handleSend}
                disabled={(!input.trim() && stagedImages.length === 0) || isTyping}
                className="p-1.5 rounded-lg bg-cyan-500/15 text-cyan-400 hover:bg-cyan-500/25 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                title="Send (Enter)"
              >
                <CornerDownLeft className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
